/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { getInitialDragMode } from './util';
import { VisDragModeControls } from './VisDragModeControls';
import { Dimension } from '#utils/UNSAFE_visTypes/common';

type Props = {
  plotAreaSpace: Dimension;
  componentWidth: number;
  isRtl: boolean;
  showPanButton: boolean;
  showZoomButton: boolean;
  showSelectButton: boolean;
  isDisabled?: boolean;
  dragMode?: 'zoom' | 'pan' | 'user' | 'select' | 'off';
};

export function useVisDragModeControls({
  componentWidth,
  plotAreaSpace,
  isRtl,
  showPanButton,
  showZoomButton,
  showSelectButton,
  isDisabled = false,
  dragMode
}: Props) {
  const dragButtonDivRef = useRef<HTMLDivElement>(null);
  const originalDragModeRef = useRef<'select' | 'zoom' | 'pan' | 'off' | 'user' | undefined>(dragMode);

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    isInitialRenderRef.current = false;
  }, []);

  const [userDragMode, setUserDragMode] = useState<'select' | 'zoom' | 'pan' | undefined>();
  const initialSelection = getInitialDragMode(
    dragMode,
    showPanButton,
    showZoomButton,
    showSelectButton
  );

  const dragModeChanged = originalDragModeRef.current != dragMode;

  if (dragModeChanged || isInitialRenderRef.current) {
    originalDragModeRef.current = dragMode;
    setUserDragMode(initialSelection);
  }

  const toggleButtonContent = isDisabled ? undefined : (
    <VisDragModeControls
      position={{
        top: `${plotAreaSpace.y}px`,
        right: isRtl ? undefined : `${componentWidth - plotAreaSpace.x - plotAreaSpace.width}px`,
        left: isRtl ? `${plotAreaSpace.x}px` : undefined
      }}
      divRef={dragButtonDivRef}
      showSelectButton={showSelectButton}
      showZoomButton={showZoomButton}
      showPanButton={showPanButton}
      dragMode={userDragMode}
      onDragModeChange={setUserDragMode}
    />
  );

  return {
    dragButtonDivRef: dragButtonDivRef,
    toggleButtonContent: toggleButtonContent,
    userDragMode
  };
}
